import React from "react";
import { Link } from "react-router-dom";

/**
 * Parses markdown links [text](url) and raw URLs (https://... or www...) into clickable elements.
 */
export function renderFormattedText(
  text,
  linkClassName = "text-emerald-600 hover:text-emerald-700 underline font-semibold transition-colors break-words"
) {
  if (!text || typeof text !== "string") return text;

  // Matches [Anchor Text](https://... or /...) OR raw URLs (https?://... or www....)
  const pattern =
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)|(https?:\/\/[^\s<]+[^<.,:;"')\]\s]|www\.[^\s<]+[^<.,:;"')\]\s])/gi;

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    // 1. Text before the matched link
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    if (match[1] && match[2]) {
      // Markdown link: [text](url)
      const anchorText = match[1];
      const url = match[2];
      const isInternal = url.startsWith("/") && !url.startsWith("//");

      if (isInternal) {
        parts.push(
          <Link key={match.index} to={url} className={linkClassName}>
            {anchorText}
          </Link>
        );
      } else {
        parts.push(
          <a
            key={match.index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
          >
            {anchorText}
          </a>
        );
      }
    } else if (match[3]) {
      // Raw URL: https://... or www....
      const rawUrl = match[3];
      const href = rawUrl.startsWith("www.") ? `https://${rawUrl}` : rawUrl;
      parts.push(
        <a
          key={match.index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
        >
          {rawUrl}
        </a>
      );
    }

    lastIndex = pattern.lastIndex;
  }

  // Remaining text after last link
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

/**
 * Renders multi-line text split by paragraphs with full hyperlink support.
 */
export default function FormattedText({
  text,
  paragraphClassName = "break-words leading-relaxed",
  linkClassName = "text-emerald-600 hover:text-emerald-700 underline font-semibold transition-colors break-words",
}) {
  if (!text) return null;

  const paragraphs = String(text)
    .split(/\n\s*\n|\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) return null;

  return (
    <>
      {paragraphs.map((para, idx) => (
        <p key={idx} className={paragraphClassName}>
          {renderFormattedText(para, linkClassName)}
        </p>
      ))}
    </>
  );
}
